import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Map as ImmutableMap } from 'immutable';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { Button, Toolbar, Grid, Collapse } from '@material-ui/core';
import { ExpandLess, ExpandMore, TextFormat, UnfoldLess, UnfoldMore } from '@material-ui/icons';
import { ColDef } from '@ag-grid-community/core';
import { Delta } from '../QueueLeaderboard';
import { HistoricalIvr, IvrState } from './IvrHud';
import DurationRenderer from './DurationRenderer';

interface Props {
  historicalIvr: ImmutableMap<string, HistoricalIvr>;
  ivrStates: IvrState[];
  ivrDeltas: Delta[];
  fontSize: string;
  defaultColDef: any;
  activeLeaderboard: 'talking' | 'interactions' | 'statistics';
  theme: string;
  gridHeight: number;
  tab: string;
  excludedIvrs: string[];
}

const IvrTalkingLeaderboard: React.FC<Props> = React.memo(
  ({
    historicalIvr,
    ivrStates,
    ivrDeltas,
    fontSize,
    defaultColDef,
    activeLeaderboard,
    theme,
    gridHeight,
    tab,
    excludedIvrs,
  }: Props) => {
    const gridRef = useRef<AgGridReact>(null);
    const [gridReady, setGridReady] = useState(false);
    const columnDefs = useMemo(
      () => [
        {
          headerName: tab === 'air' ? 'AI Agent Id' : 'IVR ID',
          field: 'ivrId',
          hide: true,
          filter: 'agSetColumnFilter',
          sortable: true,
        },
        {
          headerName: tab === 'air' ? 'AI Agent Name' : 'IVR Name',
          field: 'name',
          filter: 'agSetColumnFilter',
          sortable: true,
        },
        {
          headerName: 'Call State',
          field: 'state',
          filter: 'agTextColumnFilter',
          sortable: true,
        },
        {
          headerName: 'Duration',
          field: 'endTime',
          cellRenderer: DurationRenderer,
          sortable: true,
        },
        {
          headerName: 'Time',
          field: 'time',
          cellRenderer: (params: ICellRendererParams<any>) => new Date(params.value).toLocaleTimeString(),
          sortable: true,
        },
        {
          headerName: 'From Name',
          field: 'from.name',
          filter: 'agSetColumnFilter',
          sortable: true,
        },
        {
          headerName: 'From Phone',
          field: 'from.phoneNumber',
          filter: 'agSetColumnFilter',
          sortable: true,
        },
        {
          headerName: 'Call ID',
          field: 'callId',
          filter: 'agSetColumnFilter',
          hide: true,
          sortable: true,
        },
        {
          headerName: 'Type',
          field: 'type',
          hide: true,
          filter: 'agTextColumnFilter',
          sortable: true,
        },
      ],
      [tab],
    );

    const convertToDisplay = useCallback((state: string, isAir: boolean) => {
      let newState = state;
      if (state === 'ABANDONED') {
        if (isAir) {
          newState = 'ENDED';
        }
      }
      // Any other displayed state changes go here
      return newState;
    }, []);

    // Filter on IVR IDS
    useEffect(() => {
      if (!gridRef.current || !gridRef.current?.api || !gridReady) return;
      const filterInstance = gridRef.current.api.getFilterInstance('ivrId');
      if (filterInstance) {
        const inArray = Array.from(
          historicalIvr.filter((value, key) => !excludedIvrs.find((ivrId) => ivrId === key)).values(),
        ).map((data) => data.id);
        filterInstance.setModel({
          values: inArray,
        });
        gridRef.current.api.onFilterChanged();
      }
    }, [gridReady, excludedIvrs]);

    useEffect(() => {
      let isSubscribed = true;
      
      // Early return if conditions aren't met
      if (!gridRef.current || !gridRef.current?.api || !ivrStates.length) return;
      
      try {
        if (!isSubscribed) return;
        
        const gridApi = gridRef.current.api;
        const updates: IvrState[] = [];
        const adds: IvrState[] = [];

        // Group states by ivrId + callId and keep only the latest one
        const latestStates = new Map<string, IvrState>();
        ivrStates.forEach((state) => {
          const key = `${state.callId}#${state.ivrId}`;
          const existing = latestStates.get(key);
          const stateTime = new Date(state.time);
          
          if (existing) {
            const existingTime = new Date(existing.time);
            if (stateTime > existingTime) {
              // eslint-disable-next-line no-console
              console.debug(`Updating state for ${key}: ${existing.state} (${existing.time}) -> ${state.state} (${state.time})`);
              latestStates.set(key, state);
            } else if (state.state !== existing.state) {
              // eslint-disable-next-line no-console
              console.warn(`Ignoring out-of-order state for ${key}: ${state.state} (${state.time}) older than current ${existing.state} (${existing.time})`);
            }
          } else {
            latestStates.set(key, state);
          }
        });

        // Process the latest states
        const processedStates = Array.from(latestStates.values()).map((state) => {
          const callState = convertToDisplay(state.state, state.isAir);
          const rowId = `${state.callId}#${state.ivrId}`;
          const type = state.isAir ? 'AIR' : 'IVR';
          const name = historicalIvr.get(state.ivrId)?.name;
          return { ...state, state: callState, rowId, type, name };
        });

        // Only proceed if grid API is still available
        if (!gridApi) return;

        // Now process updates vs adds
        processedStates.forEach((state) => {
          const rowNode = gridApi.getRowNode(state.rowId);
          if (rowNode) {
            updates.push(state);
          } else {
            adds.push(state);
          }
        });

        // Double check grid API is still available before applying transactions
        if (!gridRef.current?.api) return;

        // Apply transactions
        if (adds.length > 0) {
          gridApi.applyTransaction({ add: adds });
        }
        if (updates.length > 0) {
          gridApi.applyTransaction({ update: updates });
        }
      } catch (error) {
        // Handle error silently to prevent white screening
        console.log(error);
      }
      
      // eslint-disable-next-line consistent-return
      return () => {
        isSubscribed = false;
      };
    }, [ivrStates, convertToDisplay, historicalIvr]);

    useEffect(() => {
      if (activeLeaderboard === 'talking') {
        if (tab === 'ivr') {
          const columnModelFromStorage = localStorage.getItem(`ivr-talking-columns`) || '{}';
          gridRef.current?.columnApi.applyColumnState({
            state: JSON.parse(columnModelFromStorage),
            applyOrder: true,
          });
        }
        if (tab === 'air') {
          const columnModelFromStorage = localStorage.getItem(`air-talking-columns`) || '{}';
          gridRef.current?.columnApi.applyColumnState({
            state: JSON.parse(columnModelFromStorage),
            applyOrder: true,
          });
        }
      }
    }, [gridReady, gridRef, activeLeaderboard, tab]);

    useEffect(() => {
      if (!gridRef.current || !gridRef.current?.api) return;
      const currentFilterModel = gridRef.current.api.getFilterModel() || {};
      if (tab === 'air') {
        currentFilterModel.type = {
          filterType: 'text',
          type: 'equals',
          filter: 'AIR',
        };
      } else {
        currentFilterModel.type = {
          filterType: 'text',
          type: 'equals',
          filter: 'IVR',
        };
      }
      gridRef.current.api.setFilterModel(currentFilterModel);
    }, [tab, gridReady]);

    useEffect(() => {
      if (!gridRef.current || !gridRef.current?.api) return;
      const currentFilterModel = gridRef.current.api.getFilterModel() || {};
      if (activeLeaderboard === 'talking') {
        currentFilterModel.state = {
          filterType: 'text',
          type: 'equals',
          filter: 'TALKING',
        };
      } else if (activeLeaderboard === 'interactions') {
        currentFilterModel.state = {
          filterType: 'text',
          type: 'notEqual',
          filter: 'TALKING',
        };
      }
      gridRef.current.api.setFilterModel(currentFilterModel);
    }, [activeLeaderboard, gridReady]);

    return (
      <div
        className={theme}
        style={{
          height: `${gridHeight}vh`,
          width: '100%',
          display: activeLeaderboard === 'statistics' ? 'none' : 'block',
        }}
      >
        <AgGridReact
          ref={gridRef}
          onGridReady={() => setGridReady(true)}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          suppressLoadingOverlay
          onFilterChanged={(e) => {
            const newColumnState = e.api.getFilterModel();
            if (tab === 'ivr') {
              if (activeLeaderboard === 'talking' || activeLeaderboard === 'interactions')
                localStorage.setItem(`ivr-talking-filters`, JSON.stringify(newColumnState));
            }
            if (tab === 'air') {
              if (activeLeaderboard === 'talking' || activeLeaderboard === 'interactions')
                localStorage.setItem(`air-talking-filters`, JSON.stringify(newColumnState));
            }
          }}
          onColumnResized={(e) => {
            const newColumnState = e.columnApi.getColumnState();
            if (tab === 'ivr') {
              if (activeLeaderboard === 'talking' || activeLeaderboard === 'interactions')
                localStorage.setItem(`ivr-talking-columns`, JSON.stringify(newColumnState));
            }
            if (tab === 'air') {
              if (activeLeaderboard === 'talking' || activeLeaderboard === 'interactions')
                localStorage.setItem(`air-talking-columns`, JSON.stringify(newColumnState));
            }
          }}
          onColumnMoved={(e) => {
            const newColumnState = e.columnApi.getColumnState();
            if (tab === 'ivr') {
              if (activeLeaderboard === 'talking' || activeLeaderboard === 'interactions')
                localStorage.setItem(`ivr-talking-columns`, JSON.stringify(newColumnState));
            }
            if (tab === 'air') {
              if (activeLeaderboard === 'talking' || activeLeaderboard === 'interactions')
                localStorage.setItem(`air-talking-columns`, JSON.stringify(newColumnState));
            }
          }}
          getRowId={(params) => params.data.rowId}
          rowHeight={28}
        />
      </div>
    );
  },
);

export default IvrTalkingLeaderboard;
